// =============================================================================
// ProtectedRoute — Admin route guard (requires authentication)
// =============================================================================

import { Navigate, useLocation, Outlet } from 'react-router';
import { hasAllPermissions } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { Spinner } from '@tkpod/ui';

interface ProtectedRouteProps {
  requiredPermissions?: string[];
  redirectTo?: string;
  children?: React.ReactNode;
}

export function ProtectedRoute({
  requiredPermissions,
  redirectTo = '/login',
  children,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-slate-900">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  if (requiredPermissions && requiredPermissions.length > 0) {
    const userPermissions = user?.permissions ?? user?.currentOrganization?.permissions ?? [];
    const { allowed } = hasAllPermissions(userPermissions, requiredPermissions);

    if (!allowed) {
      return (
        <div className="flex h-screen flex-col items-center justify-center gap-4 bg-slate-900">
          <h1 className="text-2xl font-bold text-white">Access Denied</h1>
          <p className="text-slate-400">
            You do not have the required permissions to access this page.
          </p>
        </div>
      );
    }
  }

  return children ? <>{children}</> : <Outlet />;
}
